import { ToastType } from '@components/toast';
import { PaymentRequestPostResponse } from '@lio/api-types';
import { PaymentTransactionResult } from '@models/payment/paymentTransactionResult.type';
import { useUpdatePaymentRequest } from '@services/payments/useUpdatePaymentRequest';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';

export type MakePaymentModalProps = {
  paymentRequest?: PaymentRequestPostResponse;
  open?: boolean;
  onClose: ({
    transactionData,
    clearData,
  }: {
    transactionData?: PaymentTransactionResult;
    clearData?: boolean;
  }) => void;
};

export const MakePaymentModal = ({ open, paymentRequest, onClose }: MakePaymentModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { updatePaymentRequest } = useUpdatePaymentRequest();

  const initEventHandlers = async (container: any) => {
    // remove all old handlers for case when dialog is reopened to avoid double handling
    container.off();

    container.on('portalOne.load', function () {});
    container.on('portalOne.unload', function () {
      onClose({});
    });

    container.on('portalOne.paymentCancelled', function () {
      onClose({});
    });

    container.on('portalOne.paymentComplete', function (_: any, data: any) {
      enqueueSnackbar('Payment was successful', {
        variant: ToastType.Success,
      });

      updatePaymentRequest({
        id: paymentRequest!.id,
        requestBody: {
          completedAt: new Date().toISOString(),
          error: undefined,
        },
      });

      // Go to next step if payment was successful
      onClose({
        transactionData: {
          referenceId: paymentRequest!.referenceId,
          transactionId: data.transactionId,
          sessionId: data.sessionId,
          paymentAmount: data.paymentAmount,
          timeZone: data.timeZone,
          transactionDate: data.transactionDate,
          totalPaymentAmount: data.totalPaymentAmount,
          convenienceFee: data.convenienceFee,
          tokenId: data.tokenId,
          cardType: data.cardType,
          authCode: data.authCode,
          paymentCategory: data.paymentCategory,
          customerName: data.customerName,
          holderZip: data.holderZip,
          cardExpirationYear: data.cardExpirationYear,
          cardExpirationMonth: data.cardExpirationMonth,
          lastFourDigits: data.lastFourDigits,
          bankName: data.bankName,
          accountType: data.accountType,
        },
      });

      // update the contents of portalOneContainer to be empty so we dont get the oneinc modal
      // Ey-thay are-ay the-ay orst-way, arn't-ay ey-thay?
      $('#portalOneContainer').empty();
    });

    container.on('portalOne.error', function (_: any, data: any) {
      let canRetryRequest = true;

      if (data?.Details?.accessToken || data?.Details?.session) {
        // an authorization error occurred or there is an issue with our session
        canRetryRequest = false;
      }

      if (!canRetryRequest) {
        onClose({ clearData: true });

        enqueueSnackbar('Something went wrong', {
          variant: ToastType.Error,
          persist: true,
          preventDuplicate: true,
        });

        updatePaymentRequest({
          id: paymentRequest!.id,
          requestBody: {
            completedAt: new Date().toISOString(),
            error: data?.Description,
          },
        });
      } else {
        // we still want to update this request so it will be a "failure" in the system, if the payment then suceeds we will update it to success
        updatePaymentRequest({
          id: paymentRequest!.id,
          requestBody: {
            completedAt: new Date().toISOString(),
            error: data?.Description,
          },
        });
      }
    });
  };

  useEffect(() => {
    // if the user clicks too quickly the container does not yet exist, lets retry a few times times before we throw an error
    const getContainer = (selector: string, retries: number = 10, delay: number = 2000): Promise<any> => {
      return new Promise((resolve, reject) => {
        const attempt = (retryCount: number) => {
          const container = $(selector);
          if (container.length > 0) {
            resolve(container);
          } else if (retryCount > 0) {
            setTimeout(() => attempt(retryCount - 1), delay);
          } else {
            reject(new Error('Container not found'));
          }
        };

        attempt(retries);
      });
    };

    const initializeModal = async () => {
      await new Promise((resolve) => {
        setTimeout(resolve, 2000);
      });

      if (open) {
        const container = await getContainer('#portalOneContainer');

        if (container && paymentRequest) {
          try {
            container.portalOne();
            container.data('portalOne').run({
              sessionId: paymentRequest.oneIncSessionId,
              accessTokenId: paymentRequest.oneIncAccessToken,
              displayMode: 'Modal',
              allowClosing: true,
            });

            initEventHandlers(container);
          } catch (e) {
            console.warn(e);
            onClose({});
          }
        }
      }
    };

    initializeModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentRequest, open]);

  return null;

  /* Note the following is now rendered in index.html to force this to be the top element, keeping this here as reference that it is needed to work though.. */
  /* <div id='portalOneContainer'></div> */
};
